// ── 캐릭터 이미지 공통 컴포넌트 ─────────────────────────────
// 목표: 칸 크기는 그대로, 캐릭터 "전신"을 기준으로 상하좌우 여백만 잘라내서 칸에 꽉 채움.
//
// 원리:
//  1) 넥슨 API 이미지는 96×96 투명 캔버스 안에 캐릭터가 작게 들어 있음.
//  2) 이미지의 투명하지 않은 픽셀 범위(= 캐릭터가 실제로 차지하는 영역)를 측정해서
//     그 영역이 칸에 딱 맞도록 확대/배치함. 모자·무기·망토 크기가 캐릭터마다 달라도 자동으로 맞춰짐.
//  3) 측정이 불가능한 경우(넥슨 서버가 픽셀 읽기를 허용하지 않을 때)는
//     발 위치를 고정(넥슨 공식 x/y 파라미터)한 뒤 일반적인 캐릭터 영역으로 잘라서 표시.
//  4) 확대될 때는 도트를 선명하게(pixelated), 축소될 때는 부드럽게 표시.
// 파라미터 출처: 넥슨 오픈API 공지 "메이플스토리 Open API 업데이트 안내" (character_image 쿼리 파라미터)

import React, { useEffect, useState } from "react";

const CANVAS = 96;
const FEET_X = 48, FEET_Y = 88;
// 측정 실패 시 쓰는 기본 캐릭터 영역 (발을 (48,88)에 고정했을 때 일반적인 전신 범위)
const FALLBACK_BOX = { x: 20, y: 12, w: 56, h: 80 };
const MAX_SCALE = 2.5; // 너무 과하게 확대되지 않도록 상한

const bboxCache = new Map(); // url → {x,y,w,h} | null

function positionedUrl(src) {
  try {
    const u = new URL(src);
    u.searchParams.set("width", String(CANVAS));
    u.searchParams.set("height", String(CANVAS));
    u.searchParams.set("x", String(FEET_X));
    u.searchParams.set("y", String(FEET_Y));
    return u.toString();
  } catch {
    return null;
  }
}

// 투명하지 않은 픽셀의 범위를 측정
function measureBBox(url) {
  if (bboxCache.has(url)) return Promise.resolve(bboxCache.get(url));
  return new Promise(resolve => {
    const done = v => { bboxCache.set(url, v); resolve(v); };
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      try {
        const w = im.naturalWidth, h = im.naturalHeight;
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const g = c.getContext("2d");
        g.drawImage(im, 0, 0);
        const d = g.getImageData(0, 0, w, h).data;
        let minX = w, minY = h, maxX = -1, maxY = -1;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          if (d[(y * w + x) * 4 + 3] > 16) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
        done(maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1, nw: w, nh: h });
      } catch {
        done(null); // 픽셀 읽기 불가 (CORS)
      }
    };
    im.onerror = () => done(null);
    im.src = url;
  });
}

export default function CharAvatar({ src, size = 40, radius, bg = "transparent", alt = "", pad = 0.06 }) {
  const posUrl = src ? positionedUrl(src) : null;
  // 0: 발 위치 고정 URL → 1: 원본 URL → 2: 빈 자리
  const [stage, setStage] = useState(posUrl ? 0 : src ? 1 : 2);
  const url = stage === 0 ? posUrl : stage === 1 ? src : null;
  const [bbox, setBBox] = useState(url && bboxCache.has(url) ? bboxCache.get(url) : undefined);

  useEffect(() => {
    let alive = true;
    if (!url) return;
    measureBBox(url).then(b => { if (alive) setBBox(b); });
    return () => { alive = false; };
  }, [url]);

  const box = {
    width: size, height: size, flexShrink: 0, position: "relative", overflow: "hidden",
    borderRadius: radius ?? (size <= 48 ? "50%" : 12), background: bg,
  };

  if (!url) {
    return <div style={{ ...box, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.45), opacity: 0.5 }}>👤</div>;
  }

  // 측정값이 있으면 그걸로, 없으면 기본 영역(원본 URL일 땐 캔버스 전체)
  const b = bbox || (stage === 0 ? { ...FALLBACK_BOX, nw: CANVAS, nh: CANVAS } : { x: 0, y: 0, w: CANVAS, h: CANVAS, nw: CANVAS, nh: CANVAS });
  const inner = size * (1 - pad * 2);
  const scale = Math.min(inner / b.w, inner / b.h, MAX_SCALE);
  const left = size / 2 - (b.x + b.w / 2) * scale;
  const top = size / 2 - (b.y + b.h / 2) * scale;

  return (
    <div style={box}>
      <img
        src={url}
        alt={alt}
        draggable={false}
        onError={() => setStage(s => s + 1)}
        style={{
          position: "absolute", maxWidth: "none",
          width: (b.nw || CANVAS) * scale, height: (b.nh || CANVAS) * scale,
          left, top,
          imageRendering: scale >= 1 ? "pixelated" : "auto",
          opacity: bbox === undefined ? 0 : 1, // 측정 끝나기 전 깜빡임 방지
          transition: "opacity .12s",
        }}
      />
    </div>
  );
}
